import {Agent, IAgentDTO} from "@/models/Agent";
import {IHttpService} from "frontend-core";
import {IAgentService} from "../IAgentService";
import {inject, injectable} from "tsyringe";
import {AgentOsagoBlockInfo, IOsagoBlockInfoDTO} from "@/models/AgentOsagoBlockInfo";
import { IOsagoCrossAgreementInfoDTO, AgentCrossAgreementInfo } from '@/models/AgentCrossAgreementInfo';
import {AgentToAddOuterDto} from "@/models/AgentToAddDto";
import {IAgentTreeDTO} from "@/modules/Segmentation/models/AgentTree";

export type IIsChildResponse = Readonly<{
    "isChild": boolean;
}>

@injectable()
export class AgentService implements IAgentService {
    constructor(
        @inject("IHttpService")
        private readonly http: IHttpService,
    ) {
    }

    async changeParent(agentId: string, parentNewId: string): Promise<void> {
        return this.http.api.put(`/agents/${agentId}/change_parent`, {
            parentId: parentNewId
        });
    }

    async isChild(id: string): Promise<boolean> {
        let res = false;

        await this.http.api.get<IIsChildResponse>(`/agents/${id}/is_child`)
            .then((data) => {
                res = data.data.isChild;
            })
            .catch(() => {
                res = false;
            })

        return res;
    }

    async getCurrent(): Promise<Agent | null> {
        let agent: Agent | null = null;

        await this.http.api.get<IAgentDTO>("/agents/current_agent")
            .then((data) => {
                agent = new Agent(data.data);
            })
            .catch(() => {
                agent = null;
            });

        return agent;
    }

    async get(id: string): Promise<Agent | null> {
        let agent: Agent | null = null;

        await this.http.api.get<IAgentDTO>(
            `/agents/${id.replace("/api/agents/", "")}`
        )
            .then((data) => {
                agent = new Agent(data.data);
            })
            .catch(() => {
                agent = null;
            });

        return agent;
    }

    async search(search: string, key?: string): Promise<Agent[]> {
        let res: Agent[] = [];

        if (!key) {
            const agent = await this.get(search);

            if (agent) {
                res.push(agent);
            }
        } else {
            const dtoArray = await this.http.api.get<IAgentDTO[]>(`/agents?${key}=${encodeURIComponent(search)}`);

            res = dtoArray.data.map(i => new Agent(i));
        }

        return res;
    }

    async getOsagoBlockInfo(userId: string): Promise<AgentOsagoBlockInfo | null> {
        let res: AgentOsagoBlockInfo | null = null;

        await this.http.api.post<IOsagoBlockInfoDTO>(
            `/user_setting/block_osago/get`,
            {
                userId: Number(userId),
            }
        ).then((data) => {
            res = new AgentOsagoBlockInfo(data.data)
        });

        return res;
    }

    async getOsagoCrossAgreementInfo(userId: string): Promise<AgentCrossAgreementInfo | null> {
        let res: AgentCrossAgreementInfo | null = null;

        await this.http.api.post<IOsagoCrossAgreementInfoDTO>(
            `/user_setting/osago_cross_agreement/get`,
            {
                userId: Number(userId),
            }
        ).then((data) => {
            res = new AgentCrossAgreementInfo(data.data)
        });

        return res;
    }

    async setOsagoCrossAgreementStatus(userId: string, allowCrossAgreement: boolean): Promise<void> {
        await this.http.api.post<void>(
            `/user_setting/osago_cross_agreement/set`,
            {
                userId: Number(userId),
                osagoCrossAgreement: allowCrossAgreement,
            }
        )
    }

    async addNewAgent(newAgentData: AgentToAddOuterDto): Promise<string> {
        let newAgentId: string = "";

        await this.http.api.post(
            "agents/add",
            {...newAgentData},
        )
            .then((res) => {
                newAgentId = res.data.id;
            })

        return newAgentId;
    }

    async setOsagoAlwaysSegment(agentId: string, isAlwaysSegment: boolean): Promise<boolean | null> {
        let status: boolean | null = null;

        await this.http.api.put<any>(
            `agents/${agentId}`,
            {
                isAlwaysSegment
            }
        ).then((res) => {
            status = res.data.isAlwaysSegment
        })

        return status;
    }

    async getAllChildren(id: string): Promise<Agent[]> {
        let array: Agent[] = [];

        await this.http.api.get<IAgentTreeDTO[]>(`/agents/${id}/children`, {
            params: {
                with_child: true,
            }
        })
        .then(async (data) => {
            array.push(
                ...data.data.map((item) => new Agent(item)),
            );
        });

        return array;
    }
}
